'use client'

import Link from 'next/link'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { usePathname } from 'next/navigation'
import { useState, useRef, useEffect } from 'react'
import { Menu, X, ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/auth-context'

const creatorsDropdown = [
  { href: '/for/gaming-youtubers', label: 'Gaming YouTubers' },
  { href: '/for/tech-reviewers', label: 'Tech Reviewers' },
  { href: '/for/educational-channels', label: 'Educational Channels' },
  { href: '/for/finance-youtubers', label: 'Finance YouTubers' },
  { href: '/for/vloggers', label: 'Vloggers' },
  { href: '/for/shorts-creators', label: 'Shorts Creators' },
  { href: '/for/podcast-clips', label: 'Podcast Clippers' },
  { href: '/for/commentary-channels', label: 'Commentary Channels' },
  { href: '/for', label: 'View All', divider: true },
]

const resourcesDropdown = [
  { href: '/tools', label: 'All Free Tools' },
  // Safety suite first, grouped: these two answer "will this cost me
  // revenue", which is the question people arrive with.
  { href: '/tools/youtube-monetization-checker', label: 'Policy Pre-Flight' },
  { href: '/tools/youtube-inauthentic-content-checker', label: 'Inauthentic Content Checker' },
  // Then the craft tools.
  { href: '/tools/youtube-channel-scanner', label: 'Channel Scanner' },
  { href: '/tools/youtube-comment-miner', label: 'Comment Miner' },
  { href: '/tools/youtube-channel-skill-generator', label: 'Channel Skill Generator' },
  { href: '/tools/youtube-title-analyzer', label: 'Title Analyzer' },
  { href: '/tools/script-word-counter', label: 'Script Word Counter', divider: true },
  { href: '/guides', label: 'All Guides' },
  { href: '/guides/check-youtube-script-before-recording', label: 'Script Review Guide' },
  { href: '/guides/best-youtube-script-analyzer', label: 'Analyzer Guide' },
  { href: '/blog/youtube-hook-study', label: '349-Video Hook Study' },
  { href: '/templates', label: 'Script Templates', divider: true },
  { href: '/hooks', label: 'Hook Libraries' },
  { href: '/compare', label: 'Comparisons', divider: true },
  { href: '/blog', label: 'Blog' },
  { href: '/breakdowns', label: 'Channel Breakdowns' },
  { href: '/faq', label: 'FAQ' },
]

function DropdownMenu({
  label,
  items,
  isActive,
}: {
  label: string
  items: { href: string; label: string; divider?: boolean }[]
  isActive: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
          isActive
            ? 'text-foreground bg-secondary'
            : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
        }`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-1.5 w-56 bg-popover border border-border rounded-md shadow-xl shadow-black/30 py-1 z-50"
          >
            {items.map((item) => (
              <div key={item.href}>
                {item.divider && <div className="border-t border-border/50 my-1" />}
                <Link
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                >
                  {item.label}
                </Link>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function Navigation() {
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileCreatorsOpen, setMobileCreatorsOpen] = useState(false)
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false)
  const { isAuthenticated } = useAuth()

  // Hide nav on certain pages
  if (pathname === '/login' || pathname === '/dashboard' || pathname?.startsWith('/dashboard')) {
    return null
  }

  const isCreatorsActive = pathname?.startsWith('/for')
  const isResourcesActive = pathname?.startsWith('/guides') || pathname?.startsWith('/compare') || pathname?.startsWith('/alternatives') || pathname?.startsWith('/blog') || pathname?.startsWith('/templates') || pathname?.startsWith('/hooks') || pathname === '/faq'

  return (
    <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 group">
          <Image
            src="/prepublish-logo-64.png"
            alt="Prepublish logo"
            width={32}
            height={32}
            // In the sticky bar, so on screen at arrival on every page.
            loading="eager"
            className="h-8 w-8 object-contain transition-transform group-hover:scale-105"
          />
          <span className="font-heading text-lg font-semibold text-foreground tracking-tight">
            Prepublish
          </span>
        </Link>

        {/* Desktop Navigation */}
        {!isAuthenticated && (
          <div className="hidden md:flex items-center gap-1">
            <Link
              href="/upload"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                pathname === '/upload'
                  ? 'text-foreground bg-secondary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              Analyze
            </Link>
            <DropdownMenu
              label="For Creators"
              items={creatorsDropdown}
              isActive={!!isCreatorsActive}
            />
            <DropdownMenu
              label="Resources"
              items={resourcesDropdown}
              isActive={!!isResourcesActive}
            />
            <Link
              href="/pricing"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                pathname === '/pricing'
                  ? 'text-foreground bg-secondary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              Pricing
            </Link>
          </div>
        )}

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <Link href="/dashboard">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
              >
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link href="/login">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                >
                  Log in
                </Button>
              </Link>
              <Link href="/upload">
                <Button
                  size="sm"
                  className="bg-foreground text-background hover:bg-foreground/90 font-medium"
                >
                  Get Started
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-border/50 bg-background"
          >
            <div className="px-4 py-4 space-y-1">
              {!isAuthenticated && (
                <>
                  <Link
                    href="/upload"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                      pathname === '/upload'
                        ? 'text-foreground bg-secondary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                    }`}
                  >
                    Analyze
                  </Link>

                  {/* For Creators accordion */}
                  <button
                    onClick={() => setMobileCreatorsOpen(!mobileCreatorsOpen)}
                    className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md transition-colors"
                  >
                    For Creators
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileCreatorsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {mobileCreatorsOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 space-y-1">
                          {creatorsDropdown.map((item) => (
                            <Link
                              key={item.href}
                              href={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md transition-colors"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Resources accordion */}
                  <button
                    onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                    className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md transition-colors"
                  >
                    Resources
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileResourcesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {mobileResourcesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 space-y-1">
                          {resourcesDropdown.map((item) => (
                            <Link
                              key={item.href}
                              href={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md transition-colors"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Link
                    href="/pricing"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                      pathname === '/pricing'
                        ? 'text-foreground bg-secondary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                    }`}
                  >
                    Pricing
                  </Link>
                </>
              )}
              <div className={`${!isAuthenticated ? 'pt-4 mt-4 border-t border-border/50' : ''} space-y-2`}>
                {isAuthenticated ? (
                  <Link
                    href="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md transition-colors"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md transition-colors"
                    >
                      Log in
                    </Link>
                    <Link href="/upload" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-foreground text-background hover:bg-foreground/90 font-medium">
                        Get Started
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}
